
import EditableInput from "@/components/ui/editable-input";
import { User, Mail, Phone, Edit, X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from '@/hooks/useI18n';
import { toast } from "@/hooks/useToast";
import { ProfileSectionSkeleton } from './ProfileSectionSkeleton';

// Profile section component with separate edit state
interface ProfileSectionProps {
  isEditing?: boolean;
  onEditToggle?: () => void;
  onCancelEdit?: () => void;
}

const ProfileSection = ({
  isEditing = false,
  onEditToggle,
  onCancelEdit
}: ProfileSectionProps) => {
  const { userData, loading, refreshUserData } = useAuth();
  const { t } = useI18n();
  const [isSaving, setIsSaving] = useState(false);

  // Local state for form data
  const [formData, setFormData] = useState({
    first_name: userData?.first_name || "",
    last_name: userData?.last_name || "",
    email: userData?.email || "",
    phone: userData?.phone || ""
  });

  // Update local state when userData changes
  useEffect(() => {
    if (userData) {
      setFormData({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        phone: userData.phone || ""
      });
    }
  }, [userData]);

  const handleInputChange = (field: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    // Reset to original userData values
    if (userData) {
      setFormData({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        phone: userData.phone || ""
      });
    }
    onCancelEdit?.();
  };

  const handleSave = async () => {
    if (!userData?.id) return;
    setIsSaving(true);
    try {
      // Se l'email è cambiata, aggiornala ovunque tramite RPC dedicata
      const emailChanged = formData.email && formData.email !== userData.email;
      if (emailChanged) {
        const { error: emailError } = await supabase.rpc('update_my_email', {
          p_new_email: formData.email
        });
        if (emailError) {
          console.error('Error updating email:', emailError);
          toast({ title: t('profile.updateError'), variant: 'destructive' });
          return;
        }
      }

      const { error } = await supabase.rpc('update_my_profile', {
        p_first_name: formData.first_name,
        p_last_name: formData.last_name,
        p_phone: formData.phone
      });

      if (error) {
        console.error('Error updating user profile:', error);
        toast({ title: t('profile.updateError'), variant: 'destructive' });
        return;
      }

      await refreshUserData();

      toast({ title: t('profile.updateSuccess'), variant: 'default' });
      onEditToggle?.();
    } catch (err) {
      console.error('Unexpected error updating user profile:', err);
      toast({ title: t('profile.updateError'), variant: 'destructive' as const });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <ProfileSectionSkeleton />;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-normal flex items-center gap-2">
            <User className="h-4 w-4" />
            {t('profile.personalData')}
          </CardTitle>
        {onEditToggle && onCancelEdit && (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? t('common.saving') : t('common.save')}
                </Button>
              </>
            ) : (
              <Button
                onClick={onEditToggle}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                {t('common.edit')}
              </Button>
            )}
          </div>
        )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <EditableInput
          label={t('profile.firstName')}
          value={formData.first_name}
          icon={User}
          isEditing={isEditing}
          onChange={handleInputChange('first_name')}
          placeholder={t('register.placeholderFirstName')}
        />
        <EditableInput
          label={t('profile.lastName')}
          value={formData.last_name}
          icon={User}
          isEditing={isEditing}
          onChange={handleInputChange('last_name')}
          placeholder={t('register.placeholderLastName')}
        />
      </div>

      {/* Email + Phone */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <EditableInput
          label={t('auth.email')}
          value={formData.email}
          type="email"
          icon={Mail}
          isEditing={isEditing}
          onChange={handleInputChange('email')}
          placeholder={t('register.placeholderEmail')}
        />
        <EditableInput
          label={t('profile.phone')}
          value={formData.phone}
          type="tel"
          icon={Phone}
          isEditing={isEditing}
          onChange={handleInputChange('phone')}
          placeholder={t('register.placeholderPhone')}
        />
      </div>
      </CardContent>
    </Card>
  );
};

export default ProfileSection;
